<!-- Page Header -->
<div class="mb-4">
  <h1 class="h2 mb-1 fw-bold">CropFlight</h1>
  <p class="text-muted mb-0">Your aerial records, compliance, and flight activity at a glance</p>
</div>

<!-- Stat Cards Row -->
<div class="row g-3 mb-4">
  <!-- Total Acres Sprayed -->
  <div class="col-12 col-sm-6 col-lg-4">
    <div class="card stat-card">
      <div class="card-body p-3 d-flex flex-column gap-2">
        <div class="stat-icon text-primary">
          <i class="bi bi-layers"></i>
        </div>
        @if (isLoadingStats() && isLoading()) {
          <div class="skeleton-loader stat-skeleton"></div>
        } @else {
          <h3 class="stat-value fw-bold">{{ totalAcresSprayed() | number:'1.0-1' }}</h3>
        }
        <p class="text-muted mb-0 small fw-medium">Total Acres Sprayed</p>
      </div>
    </div>
  </div>

  <!-- Upcoming Flights -->
  <div class="col-12 col-sm-6 col-lg-4">
    <div class="card stat-card">
      <div class="card-body p-3 d-flex flex-column gap-2">
        <div class="stat-icon text-warning">
          <i class="bi bi-calendar-event"></i>
        </div>
        @if (isLoadingStats() && isLoading()) {
          <div class="skeleton-loader stat-skeleton"></div>
        } @else {
          <h3 class="stat-value fw-bold">{{ upcomingFlights() }}</h3>
        }
        <p class="text-muted mb-0 small fw-medium">Upcoming Flights</p>
      </div>
    </div>
  </div>

  <!-- Total Flight Hours -->
  <div class="col-12 col-sm-6 col-lg-4">
    <div class="card stat-card">
      <div class="card-body p-3 d-flex flex-column gap-2">
        <div class="stat-icon text-info">
          <i class="bi bi-clock"></i>
        </div>
        @if (isLoadingStats() && isLoading()) {
          <div class="skeleton-loader stat-skeleton"></div>
        } @else {
          <h3 class="stat-value fw-bold">{{ flightHours() | number:'1.1-1' }}</h3>
        }
        <p class="text-muted mb-0 small fw-medium">Total Flight Hours</p>
      </div>
    </div>
  </div>
</div>

<!-- Safety Testing Information Card -->
<div class="card mb-4">
  <div class="card-body">
    <div class="d-flex align-items-start gap-3">
      <div class="rounded-circle bg-success-subtle d-flex align-items-center justify-content-center flex-shrink-0 size-48px">
        <i class="bi bi-shield-check text-success fs-5"></i>
      </div>
      <div>
        <h5 class="mb-2 fw-bold">Safety Testing</h5>
        <p class="text-muted mb-3">Safety testing is a critical component of responsible drone operations. Regular safety assessments help ensure compliance with FAA regulations, protect personnel and equipment, and maintain the highest standards of operational readiness. Completing safety tests demonstrates your commitment to safe aerial application practices.</p>
        <a routerLink="/logbook/testing/safety" class="btn btn-sm btn-outline-success">
          View Safety Tests <i class="bi bi-arrow-right ms-1"></i>
        </a>
      </div>
    </div>
  </div>
</div>

<!-- FAA and State Reports Cards -->
<div class="row g-4 mb-4">
  <!-- FAA Reports -->
  <div class="col-md-6">
    <div class="card h-100">
      <div class="card-body">
        <div class="d-flex align-items-center gap-2 mb-3">
          <div class="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center size-40px">
            <i class="bi bi-file-earmark-text text-primary"></i>
          </div>
          <h5 class="mb-0 fw-bold">FAA Reports</h5>
        </div>
        <p class="text-muted mb-3">Generate FAA-compliant reports for your aerial application operations, including flight logs and spray record summaries required for 44807 and Part 137 compliance.</p>
        <div class="mb-3">
          <small class="text-muted fw-medium">Last Generated:</small>
          @if (isLoadingReportDates() && isLoading()) {
            <div class="skeleton-loader skeleton-inline d-inline-block"></div>
          } @else {
            <span class="ms-1 fw-medium">
              @if (faaLastGenerated()) {
                {{ formatDate(faaLastGenerated()) }}
              } @else {
                No reports generated yet
              }
            </span>
          }
        </div>
        <a routerLink="/logbook/reports/faa" class="btn btn-sm btn-outline-primary">
          View FAA Reports <i class="bi bi-arrow-right ms-1"></i>
        </a>
      </div>
    </div>
  </div>

  <!-- State Reports -->
  <div class="col-md-6">
    <div class="card h-100">
      <div class="card-body">
        <div class="d-flex align-items-center gap-2 mb-3">
          <div class="rounded-circle bg-info-subtle d-flex align-items-center justify-content-center size-40px">
            <i class="bi bi-file-earmark-ruled text-info"></i>
          </div>
          <h5 class="mb-0 fw-bold">State Reports</h5>
        </div>
        <p class="text-muted mb-3">Generate state-level compliance reports for your aerial application activities. State reporting requirements vary by jurisdiction and may include pesticide application records and operator certifications.</p>
        <div class="mb-3">
          <small class="text-muted fw-medium">Last Generated:</small>
          @if (isLoadingReportDates() && isLoading()) {
            <div class="skeleton-loader skeleton-inline d-inline-block"></div>
          } @else {
            <span class="ms-1 fw-medium">
              @if (stateLastGenerated()) {
                {{ formatDate(stateLastGenerated()) }}
              } @else {
                No reports generated yet
              }
            </span>
          }
        </div>
        <a routerLink="/logbook/reports/state" class="btn btn-sm btn-outline-info">
          View State Reports <i class="bi bi-arrow-right ms-1"></i>
        </a>
      </div>
    </div>
  </div>
</div>

<!-- Quick Navigation Links Card -->
<div class="card mb-4">
  <div class="card-header py-3">
    <h6 class="mb-0 fw-bold">Quick Navigation</h6>
  </div>
  <div class="card-body p-0">
    <div class="list-group list-group-flush">
      @for (link of quickLinks; track link.route) {
        <a [routerLink]="link.route" class="list-group-item list-group-item-action py-3">
          <div class="d-flex align-items-center gap-3">
            <div class="text-muted">
              <i [class]="link.icon"></i>
            </div>
            <div>
              <h6 class="mb-0 fw-medium">{{ link.label }}</h6>
              <small class="text-muted">{{ link.description }}</small>
            </div>
            <div class="ms-auto">
              <i class="bi bi-chevron-right text-muted"></i>
            </div>
          </div>
        </a>
      }
    </div>
  </div>
</div>
